import { useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";

export const inputCls = "admin-input";

export const LABELS = {
  hero: "Hero (accueil)", marquee: "Bandeau défilant", stats: "Chiffres clés", services: "Métiers (grille)",
  projects: "Réalisations", about: "L'entreprise", process: "Méthode", contact: "Contact & devis",
  footer: "Pied de page", trust: "Ils nous font confiance", logos: "Logos", team: "Photos d'équipe",
  caption: "Légende", role: "Rôle / Fonction", field: "Équipe terrain (sans photo)", rse: "Engagements RSE", eyebrow: "Sur-titre", title: "Titre", titleLines: "Lignes du titre",
  subtitle: "Sous-titre", ctaPrimary: "Bouton principal", ctaSecondary: "Bouton secondaire",
  image: "Image", badge: "Badge", value: "Valeur", label: "Libellé", sub: "Précision", items: "Éléments",
  steps: "Étapes", tag: "Étiquette", description: "Description", id: "Identifiant", span: "Largeur (classe grille)",
  category: "Catégorie", kicker: "Sur-titre", paragraphs: "Paragraphes", phone: "Téléphone", email: "Email",
  address: "Adresse", hours: "Horaires", guarantee: "Garanties", budget: "Budget", type_travaux: "Type de travaux",
  status: "Statut", created_at: "Reçu le", legal: "Mentions légales", tagline: "Accroche",
  since: "Mention d'ancienneté", coords: "Coordonnées techniques", num: "Numéro", name: "Nom",
  telephone: "Téléphone", message: "Message", nom: "Nom",
};

const pretty = (k) => LABELS[k] || k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

function ImageField({ labelKey, value, onChange, path }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      onChange(data.url);
      toast.success("Image téléversée avec succès.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setUploading(false);
  };

  return (
    <div>
      <label className="block font-tech text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">{pretty(labelKey)}</label>
      <div className="flex gap-3 items-start">
        {value && (
          <img src={imgUrl(value)} alt="aperçu" className="h-16 w-24 rounded-md object-cover border border-black/10" data-testid={`admin-image-preview-${path}`} />
        )}
        <div className="flex-1 space-y-2 min-w-0">
          <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="URL de l'image" data-testid={`admin-field-${path}`} className={inputCls} />
          <label className="inline-flex cursor-pointer items-center gap-2 border border-black/15 rounded-md px-3.5 py-2 text-sm text-zinc-600 hover:border-citek-greenDark hover:text-citek-greenDark transition-colors">
            <Upload size={14} />
            {uploading ? "Téléversement…" : "Téléverser une image"}
            <input type="file" accept="image/*" onChange={handleFile} disabled={uploading} data-testid={`admin-upload-${path}`} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
}

export default function FieldEditor({ labelKey, value, onChange, path }) {
  if (typeof value === "string") {
    if (/image/i.test(labelKey)) return <ImageField labelKey={labelKey} value={value} onChange={onChange} path={path} />;
    const long = value.length > 80 || /desc|message|paragraph|legal/i.test(labelKey);
    return (
      <div>
        <label className="block font-tech text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">{pretty(labelKey)}</label>
        {long ? (
          <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`admin-field-${path}`} className={inputCls} />
        ) : (
          <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`admin-field-${path}`} className={inputCls} />
        )}
      </div>
    );
  }

  if (Array.isArray(value)) {
    const allStrings = value.every((v) => typeof v === "string");
    const remove = (i) => onChange(value.filter((_, j) => j !== i));
    const move = (i, dir) => {
      const next = [...value];
      const j = i + dir;
      if (j < 0 || j >= value.length) return;
      [next[i], next[j]] = [next[j], next[i]];
      onChange(next);
    };
    const add = () => {
      if (allStrings) return onChange([...value, "Nouvel élément"]);
      const model = value[value.length - 1] || {};
      const blank = JSON.parse(JSON.stringify(model, (k, v) => (typeof v === "string" ? "" : v)));
      onChange([...value, blank]);
    };
    return (
      <div>
        <label className="block font-tech text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">{pretty(labelKey)}</label>
        <div className="space-y-3">
          {value.map((item, i) => (
            <div key={i} className="border border-black/10 rounded-lg bg-[#F7F8F6] p-3.5" data-testid={`admin-item-${path}-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-tech text-[11px] uppercase tracking-[0.18em] text-citek-greenDark">{i + 1}.</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => move(i, -1)} data-testid={`admin-move-up-${path}-${i}`} className="px-2 text-sm text-zinc-500 hover:text-zinc-900" aria-label="Monter">↑</button>
                  <button type="button" onClick={() => move(i, 1)} data-testid={`admin-move-down-${path}-${i}`} className="px-2 text-sm text-zinc-500 hover:text-zinc-900" aria-label="Descendre">↓</button>
                  <button type="button" onClick={() => remove(i)} data-testid={`admin-remove-${path}-${i}`} className="px-2 text-zinc-500 hover:text-red-500" aria-label="Supprimer">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              {allStrings ? (
                <input value={item} onChange={(e) => onChange(value.map((v, j) => (j === i ? e.target.value : v)))} data-testid={`admin-field-${path}-${i}`} className={inputCls} />
              ) : (
                <div className="space-y-3">
                  {Object.entries(item).map(([k, v]) => (
                    <FieldEditor key={k} labelKey={k} value={v} path={`${path}-${i}-${k}`} onChange={(nv) => onChange(value.map((v2, j) => (j === i ? { ...v2, [k]: nv } : v2)))} />
                  ))}
                </div>
              )}
            </div>
          ))}
          <button type="button" onClick={add} data-testid={`admin-add-${path}`} className="w-full border border-dashed border-citek-greenDark/40 text-citek-greenDark rounded-md py-2.5 text-sm font-tech uppercase tracking-[0.18em] hover:bg-citek-green/10 transition-colors">
            + Ajouter
          </button>
        </div>
      </div>
    );
  }

  if (value && typeof value === "object") {
    return (
      <div className="space-y-4 border-l-2 border-citek-greenDark/30 pl-4">
        {Object.entries(value).map(([k, v]) => (
          <FieldEditor key={k} labelKey={k} value={v} path={`${path}-${k}`} onChange={(nv) => onChange({ ...value, [k]: nv })} />
        ))}
      </div>
    );
  }

  return null;
}
